import { revalidateLogic, useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { MineralType } from 'types/minerals';
import {
  type TcDcaFormValues,
  type AllLockStates,
  DEFAULT_TC_DCA_FORM_VALUES,
  DEFAULT_ALL_LOCK_STATES,
  DEFAULT_STREAM_DCA_PARAMS,
} from './types';
import { onChangeValidation } from './tc-dca-validations';

// ---------------------------------------------------------------------------
// Form creation
// ---------------------------------------------------------------------------

const useCreateTcDcaForm = (defaultValues: TcDcaFormValues) =>
  useForm({
    defaultValues,
    validationLogic: revalidateLogic({
      mode: 'change',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onChange: ({ value }: { value: TcDcaFormValues }) =>
        onChangeValidation(value),
    },
  });

type TcDcaFormInstance = ReturnType<typeof useCreateTcDcaForm>;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface TcDcaFormContextType {
  form: TcDcaFormInstance;
  lockStates: AllLockStates;
  setLockParam: (
    stream: MineralType,
    type: 'dca' | 'rampUp',
    parameter: string,
    value: number | null
  ) => void;
  unlockParam: (
    stream: MineralType,
    type: 'dca' | 'rampUp',
    parameter: string
  ) => void;
  resetStreamLocks: (stream: MineralType, type: 'dca' | 'rampUp') => void;
  resetAllLocks: () => void;
  /** Bulk-replace the entire lock state (used when pre-filling from area configs). */
  setAllLockStates: (states: AllLockStates) => void;
  /** Resets a single stream's DCA params to defaults. */
  resetStream: (stream: MineralType) => void;
  /** Resets forecast settings to defaults. */
  resetForecastSettings: () => void;
  /** Resets everything (all streams + forecast settings + locks). */
  resetAll: () => void;
  /** Whether any field has been modified from its default value. */
  isFormDirty: boolean;
  /** Whether all form validations pass (no errors). */
  isFormValid: boolean;
  /** Returns the validation error message for a given field path, if any. */
  getFieldError: (fieldPath: string) => string | undefined;
}

const TcDcaFormContext = createContext<TcDcaFormContextType | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface TcDcaFormProviderProps {
  children: React.ReactNode;
}

export const TcDcaFormProvider = ({ children }: TcDcaFormProviderProps) => {
  const form = useCreateTcDcaForm(DEFAULT_TC_DCA_FORM_VALUES);

  const isFormDirty = useStore(form.store, (s) => s.isDirty);
  const isFormValid = useStore(form.store, (s) => s.isFormValid);
  const errorMap = useStore(form.store, (s) => s.errorMap);

  const getFieldError = useCallback(
    (fieldPath: string): string | undefined => {
      const onChangeErrors = errorMap?.onChange;
      if (!onChangeErrors || typeof onChangeErrors !== 'object')
        return undefined;
      return (onChangeErrors as { fields?: Record<string, string> })?.fields?.[
        fieldPath
      ];
    },
    [errorMap]
  );

  const [lockStates, setLockStates] = useState<AllLockStates>(() =>
    structuredClone(DEFAULT_ALL_LOCK_STATES)
  );

  const setLockParam = useCallback(
    (
      stream: MineralType,
      type: 'dca' | 'rampUp',
      parameter: string,
      value: number | null
    ) => {
      if (value === null) return;
      setLockStates((prev) => ({
        ...prev,
        [stream]: {
          ...prev[stream],
          [type]: { ...prev[stream][type], [parameter]: value },
        },
      }));
    },
    []
  );

  const unlockParam = useCallback(
    (stream: MineralType, type: 'dca' | 'rampUp', parameter: string) => {
      setLockStates((prev) => {
        const { [parameter]: _, ...rest } = prev[stream][type];
        return {
          ...prev,
          [stream]: { ...prev[stream], [type]: rest },
        };
      });
    },
    []
  );

  const resetStreamLocks = useCallback(
    (stream: MineralType, type: 'dca' | 'rampUp') => {
      setLockStates((prev) => ({
        ...prev,
        [stream]: { ...prev[stream], [type]: {} },
      }));
    },
    []
  );

  const resetAllLocks = useCallback(() => {
    setLockStates(structuredClone(DEFAULT_ALL_LOCK_STATES));
  }, []);

  const setAllLockStates = useCallback(
    (states: AllLockStates) => setLockStates(states),
    []
  );

  const streamFieldKey = useCallback(
    (stream: MineralType): keyof TcDcaFormValues =>
      `${stream}DcaParams` as keyof TcDcaFormValues,
    []
  );

  const resetStream = useCallback(
    (stream: MineralType) => {
      form.setFieldValue(streamFieldKey(stream), {
        ...DEFAULT_STREAM_DCA_PARAMS,
      });
      resetStreamLocks(stream, 'dca');
      resetStreamLocks(stream, 'rampUp');
    },
    [form, streamFieldKey, resetStreamLocks]
  );

  const resetForecastSettings = useCallback(() => {
    form.setFieldValue('diFormat', DEFAULT_TC_DCA_FORM_VALUES.diFormat);
    form.setFieldValue('qiFormat', DEFAULT_TC_DCA_FORM_VALUES.qiFormat);
    form.setFieldValue(
      'forecastDurationPeriod',
      DEFAULT_TC_DCA_FORM_VALUES.forecastDurationPeriod
    );
    form.setFieldValue(
      'forecastDurationOption',
      DEFAULT_TC_DCA_FORM_VALUES.forecastDurationOption
    );
  }, [form]);

  const resetAll = useCallback(() => {
    form.reset();
    resetAllLocks();
  }, [form, resetAllLocks]);

  const value = useMemo<TcDcaFormContextType>(
    () => ({
      form,
      lockStates,
      setLockParam,
      unlockParam,
      resetStreamLocks,
      resetAllLocks,
      setAllLockStates,
      resetStream,
      resetForecastSettings,
      resetAll,
      isFormDirty,
      isFormValid,
      getFieldError,
    }),
    [
      form,
      lockStates,
      setLockParam,
      unlockParam,
      resetStreamLocks,
      resetAllLocks,
      setAllLockStates,
      resetStream,
      resetForecastSettings,
      resetAll,
      isFormDirty,
      isFormValid,
      getFieldError,
    ]
  );

  return (
    <TcDcaFormContext.Provider value={value}>
      {children}
    </TcDcaFormContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useTcDcaForm = (): TcDcaFormContextType => {
  const ctx = useContext(TcDcaFormContext);
  if (!ctx) {
    throw new Error('useTcDcaForm must be used within a TcDcaFormProvider');
  }
  return ctx;
};
